import './index.scss';
import 'react-app-polyfill/ie11';
import 'react-app-polyfill/stable';

import React, { Suspense } from 'react';
import ReactDOM from 'react-dom';
import { asyncWithLDProvider } from 'launchdarkly-react-client-sdk';
import { ConnectedRouter } from 'connected-react-router';
import { createBrowserHistory } from 'history';
import throttle from 'lodash/throttle';
import { Provider as ReduxProvider } from 'react-redux';
import ZLLoader from './components/loader/loader';
import configureStore from './store/configureStore';
import { loadState, saveState } from './store/localStorage';
import { writeAccessCode } from './store/sessionAccessCode';
import { IApplicationState } from './store';
import { Layout } from 'antd';
import App from './App';
import Nav from './components/nav/nav';
import { ApolloClientProvider } from './service/apollo-client';
import FooterBar from './components/nav/FooterBar';

import('./i18n');

// Normalise malformed URLs with consecutive slashes (e.g. from marketing email links)
// so React Router can match routes correctly.
if (window.location.pathname.includes('//')) {
	const clean = window.location.pathname.replace(/\/{2,}/g, '/');
	window.history.replaceState(null, '', clean + window.location.search + window.location.hash);
}

const baseUrl = document.getElementsByTagName('base')[0].getAttribute('href') as string;
const history = createBrowserHistory({ basename: baseUrl });

const store = configureStore(history, loadState(history));

store.subscribe(
    throttle(() => {
        const state = store.getState();

        //session scoped, so it lives in sessionStorage rather than the localStorage state blob
        writeAccessCode(state.sites.accessCode);

        saveState({
            sites: {
                selectedSite: state.sites.selectedSite,
                selectedSiteGtmKey: state.sites.selectedSiteGtmKey,
                selectedSiteUI: state.sites.selectedSiteUI,
                bookingOptions: state.sites.bookingOptions,
            },
            players: {
                selectedPlayers: state.players.selectedPlayers,
                selectedPlayersUI: state.players.selectedPlayersUI,
                selectedPrivateUpgrade: state.players.selectedPrivateUpgrade,
            },
            session: {
                selectedDate: state.session.selectedDate,
                selectedTime: state.session.selectedTime,
                selectedSession: state.session.selectedSession,
            },
            invoice: {
                invoiceId: state.invoice.invoiceId,
                siteId: state.invoice.siteId,
                charge: state.invoice.charge,
                paymentStatusTypeId: state.invoice.paymentStatusTypeId,
                appliedCodes: state.invoice.appliedCodes ?? [],
            },
            premiumPtv: {
                price: state.premiumPtv.price,
                selectedPlayers: state.premiumPtv.selectedPlayers,
                creditPlayerCount: state.premiumPtv.creditPlayerCount,
                purchasePlayerCount: state.premiumPtv.purchasePlayerCount,
                purchaseName: state.premiumPtv.purchaseName,
            },
            ui: {},
            payments: {
                loading: false,
                clientSecret: state.payments.clientSecret,
            },
            customer: {
                emailAddress: state.customer.emailAddress,
                fullName: state.customer.fullName,
                phoneNumber: state.customer.phoneNumber,
                countryCode: state.customer.countryCode,
                callingCode: state.customer.callingCode,
                emailSubscribed: state.customer.emailSubscribed,
                smsSubscribed: state.customer.smsSubscribed,
            },
            booking: {
                booking: state.booking.booking,
                charge: state.booking.charge,
                appliedCodes: state.booking.appliedCodes,
                status: state.booking.status,
                loading: false,
                failed: false,
                priceSets: state.booking.priceSets,
                readyForPayment: state.booking.readyForPayment,
                selectedAddOns: state.booking.selectedAddOns,
                selectedDigitalAddOns: state.booking.selectedDigitalAddOns,
                availableAddOns: state.booking.availableAddOns,
                availableDigitalAddOns: state.booking.availableDigitalAddOns,
                bookingAddOns: state.booking.bookingAddOns,
                bookingDigitalAddOns: state.booking.bookingDigitalAddOns,
            },
            intl: {
                selectedLanguage: state.intl.selectedLanguage,
            },
            purchaseType: {
                selectedPurchaseType: state.purchaseType.selectedPurchaseType,
                selectedPurchaseTypeUI: state.purchaseType.selectedPurchaseTypeUI,
            },
            giftVoucher: {
                loading: false,
                failed: false,
                configLoading: false,
                configDetails: state.giftVoucher.configDetails,
                charge: state.giftVoucher.charge,
                giftVoucher: state.giftVoucher.giftVoucher,
                status: state.giftVoucher.status,
                selectedFixedAmountButtonIndex: state.giftVoucher.selectedFixedAmountButtonIndex,
                amount: state.giftVoucher.amount,
                toEmail: state.giftVoucher.toEmail,
                toFullName: state.giftVoucher.toFullName,
                message: state.giftVoucher.message,
                selectedGiftVoucherAmountUI: state.giftVoucher.selectedGiftVoucherAmountUI ?? false,
                selectedGiftVoucherDetailsUI: state.giftVoucher.selectedGiftVoucherDetailsUI ?? false,
                toRecipientSelected: state.giftVoucher.toRecipientSelected,
                selectedVoucherCurrency: state.giftVoucher.selectedVoucherCurrency,
                recipientDetailsValid: state.giftVoucher.recipientDetailsValid,
            },
            attribution: {
                utm_Id: state.attribution.utm_Id,
                utm_campaign: state.attribution.utm_campaign,
                utm_content: state.attribution.utm_content,
                utm_medium: state.attribution.utm_medium,
                utm_source: state.attribution.utm_source,
                utm_term: state.attribution.utm_term,
                internal_source: state.attribution.internal_source,
                internal_sourcePage: state.attribution.internal_sourcePage,
                internal_componentType: state.attribution.internal_componentType,
                internal_componentInternalName: state.attribution.internal_componentInternalName,
                internal_componentTitle: state.attribution.internal_componentTitle,
                internal_buttonText: state.attribution.internal_buttonText,
            },
            microCopy: {
                microCopyMap: {},
                microCopyBundles: {},
            },
            featureFlags: {
                flags: state.featureFlags.flags,
                lastLoadedVenueId: state.featureFlags.lastLoadedVenueId,
                flagVenueSettings: state.featureFlags.flagVenueSettings,
                loading: state.featureFlags.loading,
                hasLoaded: state.featureFlags.hasLoaded,
                abandoned: state.featureFlags.abandoned,
                userSessionId : state.featureFlags.userSessionId
            },
            paymentOption: {
                selectedOption: state.paymentOption.selectedOption,
                splitCreated: state.paymentOption.splitCreated,
                splitLoading: state.paymentOption.splitLoading,
                splitShares: state.paymentOption.splitShares,
                splitSlotCount: state.paymentOption.splitSlotCount
            }

        } as IApplicationState);
    }, 1000)
);

(async () => {
    const LDProvider = await asyncWithLDProvider({ clientSideID: process.env.REACT_APP_LAUNCH_DARKLY ?? '' });

    ReactDOM.render(
        <ReduxProvider store={store}>
            <ApolloClientProvider>
                <ConnectedRouter history={history}>
                    <LDProvider>
                        <Suspense fallback={<ZLLoader />}>
                            <Layout style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
                                <Nav />
                                <App />
                                <FooterBar />
                            </Layout>
                        </Suspense>
                    </LDProvider>
                </ConnectedRouter>
            </ApolloClientProvider>
        </ReduxProvider>,
        document.getElementById('root')
    );
})();

// If you want your app to work offline and load faster, you can change
// unregister() to register() below. Note this comes with some pitfalls.
// Learn more about service workers: https://bit.ly/CRA-PWA
//serviceWorker.unregister();
